import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AlertCircle, Calendar, Phone, Shield, Clock, User, Activity, Bell } from 'lucide-react';
import { toast } from 'sonner';
import RemindersTab from '@/components/RemindersTab';
import ContactsTab from '@/components/ContactsTab';
import SettingsTab from '@/components/SettingsTab';

export default function Dashboard({ users, currentUser, setCurrentUser, refreshUsers }) {
  const navigate = useNavigate();
  const [reminders, setReminders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [emergencyLogs, setEmergencyLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (currentUser) {
      fetchData();
    }
  }, [currentUser]);

  const fetchData = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      const [remindersRes, contactsRes, logsRes] = await Promise.all([
        axios.get(`${API}/reminders/${currentUser.id}`),
        axios.get(`${API}/emergency-contacts/${currentUser.id}`),
        axios.get(`${API}/emergency/logs/${currentUser.id}`)
      ]);
      
      setReminders(remindersRes.data);
      setContacts(contactsRes.data);
      setEmergencyLogs(logsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getTodayReminders = () => {
    const today = new Date().toDateString();
    return reminders.filter(r => new Date(r.scheduled_time).toDateString() === today);
  };

  const getPendingReminders = () => {
    return reminders.filter(r => r.status === 'pending').length;
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Welcome to Senior Voice Assistant</CardTitle>
            <CardDescription>Create a user profile to get started</CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              data-testid="create-first-user-btn"
              onClick={() => navigate('/users')} 
              className="w-full"
            >
              Create User Profile
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <div className="bg-neutral-900/80 backdrop-blur-xl border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <img 
                src="/logo.svg" 
                alt="Senior Voice Assistant Logo" 
                className="w-14 h-14 rounded-full shadow-md"
              />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Caregiver Dashboard</h1>
                <p className="text-gray-600 mt-1">Manage and monitor senior care</p>
              </div>
              {users.length > 1 && (
                <div className="ml-6">
                  <select
                    data-testid="user-selector"
                    value={currentUser?.id || ''}
                    onChange={(e) => {
                      const user = users.find(u => u.id === e.target.value);
                      setCurrentUser(user);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/households')}
              >
                <User className="w-4 h-4 mr-2" />
                Haushalte (Hayat)
              </Button>
              <Button
                data-testid="manage-users-btn"
                variant="outline"
                onClick={() => navigate('/users')}
              >
                <User className="w-4 h-4 mr-2" />
                Manage Users
              </Button>
              <Button
                data-testid="senior-view-btn"
                onClick={() => navigate(`/senior/${currentUser.id}`)}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <Activity className="w-4 h-4 mr-2" />
                Senior View
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card data-testid="stat-card-reminders" className="dashboard-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <Bell className="w-8 h-8 text-blue-600" />
                <Badge variant="secondary">{getPendingReminders()}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-gray-900">{getTodayReminders().length}</p>
              <p className="text-sm text-gray-600 mt-1">Today's Reminders</p>
            </CardContent>
          </Card>

          <Card data-testid="stat-card-contacts" className="dashboard-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <Phone className="w-8 h-8 text-green-600" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-gray-900">{contacts.length}</p>
              <p className="text-sm text-gray-600 mt-1">Emergency Contacts</p>
            </CardContent>
          </Card>

          <Card data-testid="stat-card-emergencies" className="dashboard-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-gray-900">{emergencyLogs.length}</p>
              <p className="text-sm text-gray-600 mt-1">Emergency Logs</p>
            </CardContent>
          </Card>

          <Card data-testid="stat-card-modules" className="dashboard-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <Shield className="w-8 h-8 text-purple-600" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-gray-900">
                {Object.values(currentUser.modules || {}).filter(Boolean).length}
              </p>
              <p className="text-sm text-gray-600 mt-1">Active Modules</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Card className="dashboard-card">
          <Tabs defaultValue="reminders" className="w-full">
            <CardHeader>
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger data-testid="tab-reminders" value="reminders">
                  <Calendar className="w-4 h-4 mr-2" />
                  Reminders
                </TabsTrigger>
                <TabsTrigger data-testid="tab-contacts" value="contacts">
                  <Phone className="w-4 h-4 mr-2" />
                  Contacts
                </TabsTrigger>
                <TabsTrigger data-testid="tab-emergency" value="emergency">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Emergency
                </TabsTrigger>
                <TabsTrigger data-testid="tab-settings" value="settings">
                  <Shield className="w-4 h-4 mr-2" />
                  Settings
                </TabsTrigger>
              </TabsList>
            </CardHeader>

            <CardContent>
              <TabsContent value="reminders">
                <RemindersTab 
                  userId={currentUser.id} 
                  reminders={reminders}
                  refreshData={fetchData}
                />
              </TabsContent>

              <TabsContent value="contacts">
                <ContactsTab 
                  userId={currentUser.id}
                  contacts={contacts}
                  refreshData={fetchData}
                />
              </TabsContent>

              <TabsContent value="emergency">
                <div data-testid="emergency-logs-section">
                  <h3 className="text-lg font-semibold mb-4">Emergency Activity Log</h3>
                  {emergencyLogs.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <AlertCircle className="w-16 h-16 mx-auto mb-4 opacity-30" />
                      <p>No emergency events recorded</p>
                    </div>
                  ) : (
                    <ScrollArea className="h-[400px]">
                      <div className="space-y-3">
                        {emergencyLogs.map((log) => (
                          <Card key={log.id} className="border-l-4 border-l-red-500">
                            <CardContent className="pt-4">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-semibold text-red-600">
                                    Emergency Triggered ({log.trigger_type})
                                  </p>
                                  <p className="text-sm text-gray-600 mt-1">
                                    Contacts notified: {log.contacts_notified.join(', ')}
                                  </p>
                                  <div className="flex items-center gap-2 mt-2">
                                    <Clock className="w-4 h-4 text-gray-400" />
                                    <span className="text-xs text-gray-500">
                                      {new Date(log.timestamp).toLocaleString()}
                                    </span>
                                  </div>
                                </div>
                                <Badge variant={log.status === 'resolved' ? 'default' : 'destructive'}>
                                  {log.status}
                                </Badge>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="settings">
                <SettingsTab 
                  user={currentUser}
                  refreshData={fetchData}
                  refreshUsers={refreshUsers}
                />
              </TabsContent>
            </CardContent>
          </Tabs>
        </Card>
      </div>
    </div>
  );
}